/**
 * `/parent/announcements` — the Parent Portal announcements view (P-SCH-V1,
 * ADR-0103).
 *
 * The course broadcasts posted to the courses the selected child is an active
 * learner in, newest first (pinned first), from the new
 * `learning.learner.guardian.childAnnouncements`. Each announcement is a card
 * (title · course · published-date · body). Read-only — a guardian reads, never
 * posts. The body is rendered as plain pre-wrapped text (no markdown engine on
 * the parent surface — it stays calm and dependency-free).
 */
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { inferRouterOutputs } from '@trpc/server';
import { Megaphone, Pin } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
  StatusPill,
} from '@mangrove/ui';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { ParentRoute } from '../components/parent-portal/parent-shell.js';
import { useSelectedChild, type ParentChild } from '../components/parent-portal/child-selector.js';
import {
  ChildScaffold,
  InlineError,
  formatDate,
} from '../components/parent-portal/parent-shared.js';

export const Route = createFileRoute('/parent/announcements')({
  component: ParentAnnouncementsPage,
});

type AnnouncementRow =
  inferRouterOutputs<AppRouter>['learning']['learner']['guardian']['childAnnouncements']['rows'][number];

function ParentAnnouncementsPage(): React.JSX.Element {
  return (
    <ParentRoute title="Announcements">
      <ParentAnnouncementsContent />
    </ParentRoute>
  );
}

function ParentAnnouncementsContent(): React.JSX.Element {
  const { query, children, selected, setSelected } = useSelectedChild();
  return (
    <ChildScaffold
      heroTitle="Announcements"
      heroSubtitle="Course broadcasts from your student’s instructors."
      query={query}
      children={children}
      selected={selected}
      setSelected={setSelected}
      renderBody={(child) => <AnnouncementsList child={child} />}
    />
  );
}

function AnnouncementsList({ child }: { child: ParentChild }): React.JSX.Element {
  const announcementsQ = trpc.learning.learner.guardian.childAnnouncements.useQuery({
    studentUserId: child.studentUserId,
  });
  const rows = announcementsQ.data?.rows ?? [];

  if (announcementsQ.isPending) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }, (_, i) => (
          <Card key={i} className="card-soft">
            <CardHeader className="pb-2">
              <Skeleton className="h-5 w-1/2" />
            </CardHeader>
            <CardContent className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (announcementsQ.isError) {
    return (
      <Card className="card-soft">
        <CardContent className="p-5">
          <InlineError onRetry={() => void announcementsQ.refetch()} />
        </CardContent>
      </Card>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Megaphone}
        title="No announcements yet"
        description="Instructor announcements from your student’s courses will appear here."
      />
    );
  }

  return (
    <div className="ls-stagger space-y-4">
      {rows.map((row) => (
        <AnnouncementCard key={row.id} row={row} />
      ))}
    </div>
  );
}

function AnnouncementCard({ row }: { row: AnnouncementRow }): React.JSX.Element {
  return (
    <Card className="card-soft animate-fade-in-up border-l-2 border-l-primary/40">
      <CardHeader className="gap-1.5 pb-2">
        <div className="flex flex-wrap items-center gap-2">
          {row.isPinned ? <StatusPill label="Pinned" tone="accent" icon={Pin} size="sm" /> : null}
          <StatusPill label={row.courseTitle} tone="neutral" size="sm" />
          <span className="text-xs text-muted-foreground">{formatDate(row.publishedAt)}</span>
        </div>
        <CardTitle className="text-lg">{row.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
          {row.body}
        </p>
      </CardContent>
    </Card>
  );
}
